import {SelectionRange, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeShouldShowDragHandles,
  computeTableDraggingState,
} from 'notebook/tables/store/logic/TableReorderingLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

import {TableDimensionsModel} from './TableDimensionsSelectors';

/**
 * Model containing derived state for row/column reordering (drag handles and the active drag).
 */
export interface TableReorderingModel {
  /**
   * Whether the row reordering drag handle should be shown.
   */
  shouldShowRowDragHandles: boolean;

  /**
   * Whether the column reordering drag handle should be shown.
   */
  shouldShowColumnDragHandles: boolean;

  /**
   * The dimension currently being dragged, or null when nothing is being dragged.
   */
  draggingDimension: TableDimension | null;

  /**
   * The range currently being dragged, or null when nothing is being dragged.
   */
  draggingRange: SelectionRange | null;
}

/**
 * Creates a memoized selector that computes the TableReorderingModel from the store.
 */
export function createTableReorderingSelector(
  tableDimensionsSelector: (store: TableEditableStore) => TableDimensionsModel
): (store: TableEditableStore) => TableReorderingModel {
  return createTableStoreSelector(
    {
      locked: (store) => store.stableProps.locked,
      fileId: (store) => store.stableProps.fileId,
      isProtected: (store) => store.stableProps.isProtected ?? false,
      allowReorderDimensions: (store) => store.stableProps.allowReorderDimensions,
      table: (store) => store.volatileProps.table,
      canModifyRowDim: (store) => tableDimensionsSelector(store).canModifyRowDim,
      canModifyColumnDim: (store) => tableDimensionsSelector(store).canModifyColumnDim,
      draggingDimension: (store) => store.state.draggingDimension,
      multiStartIndex: (store) => store.state.multiStartIndex,
      multiEndIndex: (store) => store.state.multiEndIndex,
    },
    ({
      locked,
      fileId,
      isProtected,
      allowReorderDimensions,
      table,
      canModifyRowDim,
      canModifyColumnDim,
      draggingDimension,
      multiStartIndex,
      multiEndIndex,
    }): TableReorderingModel => {
      const dragHandlesParams = {
        locked,
        fileId,
        isProtected,
        allowReorderDimensions,
        table,
        canModifyRowDim,
        canModifyColumnDim,
      };
      const {dimension, range} = computeTableDraggingState(
        locked,
        draggingDimension,
        multiStartIndex,
        multiEndIndex
      );
      return {
        shouldShowRowDragHandles: computeShouldShowDragHandles(TableDimension.ROW, dragHandlesParams),
        shouldShowColumnDragHandles: computeShouldShowDragHandles(TableDimension.COLUMN, dragHandlesParams),
        draggingDimension: dimension,
        draggingRange: range,
      };
    }
  );
}
